import '../index.scss';
import { FC } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import cx from 'classnames';
import { ListItem, Divider, Tooltip } from '@material-ui/core';
import { Checkbox } from '@components/SharedComponents/Checkbox';
import { translate, getTrimmedString } from '@utils';
import { MgState, SubmissionEntityInterface } from '@types';
import { setCheckedSubmissions } from '@actions/submissionActions';
import { GeneralTemplatesIcon, PersonalTemplatesIcon } from '@images/icons';
import { showCheckboxListItemSelector } from '@selectors/submission';

interface SubmissionsListItemProps {
  path: string;
  item: SubmissionEntityInterface;
  currentUserId: string;
}

export const SubmissionsListItem: FC<SubmissionsListItemProps> = ({
  path,
  item,
  currentUserId,
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const showCheckboxListItem = useSelector(showCheckboxListItemSelector);

  const checkedSubmissions = useSelector<MgState, string[]>(
    ({ submission }) => submission.checkedSubmissions,
    shallowEqual
  );

  const isDisabledLink = useSelector<MgState, boolean>(
    ({ submission }) =>
      submission.isCurrentSubmissionLoading || submission.isSubmissionUpdateLoading
  );

  const handleCheckboxChange = () => {
    const isChecked = checkedSubmissions.includes(item.id);

    const result = isChecked
      ? checkedSubmissions.filter((id) => id !== item.id)
      : [...checkedSubmissions, item.id];
    dispatch(setCheckedSubmissions(result));
  };

  const handleListItemClick = (event) => {
    if (event.target.type === 'checkbox') {
      event.preventDefault();
      handleCheckboxChange();
      return;
    }
    if (isDisabledLink) {
      event.preventDefault();
      return;
    }
  };

  const groupNames = item?.groups?.reduce((prev, { name }) => `${name},${prev}`, '').slice(0, -1);
  const trimmedGroupNames = getTrimmedString(groupNames, 20);
  return (
    <Link
      to={path}
      onClick={(event) => handleListItemClick(event)}
      data-testid="submissions-list-item"
    >
      <ListItem button className={cx('template-item', { 'chosen-item': id === item.id })}>
        {showCheckboxListItem && (
          <Checkbox id="submission-checkbox" checked={checkedSubmissions?.includes(item.id)} />
        )}
        <span className="template-item__title">
          <Tooltip title={item.title} placement="top-start" arrow>
            <span>{item.title}</span>
          </Tooltip>
        </span>
        <Tooltip title={groupNames} placement="top-start" arrow>
          <span className="template-item__tag template-item__group">{trimmedGroupNames}</span>
        </Tooltip>
        <div
          className={cx('template-item__tag', 'template-item__owner', {
            'chosen-tag': id === item.id,
          })}
        >
          {item.isCommon ? (
            <>
              <GeneralTemplatesIcon className="template-item__icon" />
              <div>{translate('common')}</div>
            </>
          ) : (
            <>
              <PersonalTemplatesIcon className="template-item__icon" />
              <div>
                {currentUserId === item.owner?.id
                  ? translate('own')
                  : item.owner?.fullNameEng || translate('unknown')}
              </div>
            </>
          )}
        </div>
        {item.isSample && (
          <span
            className={cx('template-item__tag', 'template-item__sample', {
              'chosen-tag': id === item.id,
            })}
          >
            #{translate('sample')}
          </span>
        )}
      </ListItem>
      <Divider />
    </Link>
  );
};
